import { Controller } from "@hotwired/stimulus"

const ACTIVE_PILL = "bg-teal-50 border-thq-green text-teal-700"
const REOPEN_LOCATION_KEY = "thq:events:reopen-location-menu"
const PILL_BASE = "inline-flex items-center gap-2 h-9 px-3 border rounded-lg text-sm font-medium transition-colors "

export default class extends Controller {
  static targets = [
    "rangeField", "fromField", "toField",
    "latField", "lngField", "placeField", "radiusField",
    "locationButton", "locationMenu",
    "postcode", "goButton", "radiusSection", "radiusValue", "radiusSlider",
    "datesButton", "datesLabel", "datesMenu", "customSection",
    "fromDate", "toDate",
  ]

  static values = {
    geocodeUrl: String,
    dateFormat: String,
    customLabel: String,
    kmLabel: String,
    notFoundLabel: String,
    lookupFailedLabel: String,
    goLabel: String,
  }

  connect() {
    this.initDatepickers()
    this.lookupInFlight = false
    this.initialGoLabel = this.hasGoButtonTarget ? this.goButtonTarget.textContent.trim() : ""
    this.restoreLocationMenu()
  }

  disconnect() {
    this.destroyDatepickers()
    this.closeMenus()
  }

  submit() {
    if (this.element.requestSubmit) this.element.requestSubmit()
    else this.element.submit()
  }

  // The toolbar lives inside the results turbo-frame, so a filter submit replaces
  // this controller's element and drops any open menu with it.
  submitKeepingLocationOpen() {
    try { window.sessionStorage.setItem(REOPEN_LOCATION_KEY, "1") } catch { /* private mode */ }
    this.submit()
  }

  restoreLocationMenu() {
    let flagged = false
    try {
      flagged = window.sessionStorage.getItem(REOPEN_LOCATION_KEY) === "1"
      if (flagged) window.sessionStorage.removeItem(REOPEN_LOCATION_KEY)
    } catch { /* private mode */ }
    if (flagged && this.hasLocationMenuTarget) this.togglePanel(this.locationMenuTarget)
  }

  // A GET form serialises every enabled field, so empty ones would litter the URL.
  // Browsers build the payload after submit handlers run, so disabling here is safe.
  prepare() {
    const custom = this.rangeFieldTarget.value === "custom"
    if (!custom) {
      this.fromFieldTarget.value = ""
      this.toFieldTarget.value = ""
    }
    if (this.latFieldTarget.value === "" || this.lngFieldTarget.value === "") {
      this.placeFieldTarget.value = ""
      this.radiusFieldTarget.value = ""
    }
    this.element.querySelectorAll("input[name]").forEach((input) => {
      input.disabled = input.value === ""
    })
    const sources = Array.from(this.element.querySelectorAll('input[name="source[]"]'))
    if (sources.length && sources.every((box) => box.checked)) sources.forEach((box) => { box.disabled = true })
  }

  // --- dates ---------------------------------------------------------------

  pickRange(event) {
    const range = event.currentTarget.dataset.range
    this.rangeFieldTarget.value = range
    if (range !== "custom") {
      this.submit()
      return
    }
    if (this.hasCustomSectionTarget) this.customSectionTarget.classList.remove("hidden")
    if (this.hasDatesButtonTarget) this.datesButtonTarget.className = PILL_BASE + ACTIVE_PILL
    if (this.hasDatesLabelTarget) this.datesLabelTarget.textContent = this.customLabelValue
  }

  initDatepickers() {
    if (!window.Datepicker || !this.hasFromDateTarget || !this.hasToDateTarget) return
    const opts = { autohide: true, format: this.dateFormatValue || "yyyy-mm-dd", orientation: "bottom left" }
    const build = (target, field) => {
      const dp = new window.Datepicker(target, opts)
      const iso = target.dataset.iso
      if (iso) {
        const d = new Date(`${iso}T00:00:00`)
        if (!isNaN(d)) dp.setDate(d)
      }
      target.addEventListener("changeDate", (e) => {
        const picked = e && e.detail ? e.detail.date : null
        field.value = picked && !isNaN(picked) ? this.ymd(picked) : ""
      })
      return dp
    }
    this.fromDp = build(this.fromDateTarget, this.fromFieldTarget)
    this.toDp = build(this.toDateTarget, this.toFieldTarget)
  }

  destroyDatepickers() {
    [this.fromDp, this.toDp].forEach((dp) => { if (dp && dp.destroyAndRemoveInstance) dp.destroyAndRemoveInstance() })
    this.fromDp = null
    this.toDp = null
  }

  ymd(d) {
    const p = (n) => String(n).padStart(2, "0")
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
  }

  // --- location ------------------------------------------------------------

  onPostcodeKey(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    this.goPostcode()
  }

  onRadiusInput(event) {
    this.radiusFieldTarget.value = event.target.value
    if (this.hasRadiusValueTarget) this.radiusValueTarget.textContent = this.km(event.target.value)
  }

  km(n) {
    return (this.kmLabelValue || "%{count} km").replace("%{count}", n)
  }

  goPostcode() {
    if (this.lookupInFlight) return
    const place = this.hasPostcodeTarget ? this.postcodeTarget.value.trim() : ""
    if (!place) return
    this.lookupInFlight = true
    if (this.hasGoButtonTarget) this.goButtonTarget.textContent = "…"
    const done = () => {
      this.lookupInFlight = false
      if (this.hasGoButtonTarget) this.goButtonTarget.textContent = this.goLabelValue || this.initialGoLabel
    }
    const url = `${this.geocodeUrlValue}?q=${encodeURIComponent(place)}`
    fetch(url, { headers: { Accept: "application/json" } })
      .then((res) => {
        if (res.status === 404 || res.status === 422) {
          done()
          window.alert(`${this.notFoundLabelValue} ${place}`)
          return null
        }
        if (!res.ok) {
          done()
          window.alert(this.lookupFailedLabelValue)
          return null
        }
        return res.json()
      })
      .then((data) => {
        if (!data) return
        done()
        const lat = Number(data.latitude)
        const lng = Number(data.longitude)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          window.alert(this.lookupFailedLabelValue)
          return
        }
        this.latFieldTarget.value = String(lat)
        this.lngFieldTarget.value = String(lng)
        this.placeFieldTarget.value = place
        this.submitKeepingLocationOpen()
      })
      .catch(() => {
        done()
        window.alert(this.lookupFailedLabelValue)
      })
  }

  clearLocation() {
    this.latFieldTarget.value = ""
    this.lngFieldTarget.value = ""
    this.placeFieldTarget.value = ""
    if (this.hasPostcodeTarget) this.postcodeTarget.value = ""
    this.submit()
  }

  // --- dropdown menus ------------------------------------------------------

  toggleLocationMenu(event) { if (event) event.stopPropagation(); this.togglePanel(this.hasLocationMenuTarget ? this.locationMenuTarget : null) }
  toggleDatesMenu(event) { if (event) event.stopPropagation(); this.togglePanel(this.hasDatesMenuTarget ? this.datesMenuTarget : null) }

  toggleMenu(event) {
    if (event) event.stopPropagation()
    const wrapper = event.currentTarget.closest("[data-ef-menu]")
    this.togglePanel(wrapper ? wrapper.querySelector("[data-ef-panel]") : null)
  }

  togglePanel(panel) {
    const wasOpen = panel && !panel.classList.contains("hidden")
    this.closeMenus()
    if (!panel || wasOpen) return
    panel.classList.remove("hidden")
    this.alignMenu(panel)
    const focusable = panel.querySelector("input[type=search], [data-ef-autofocus]")
    if (focusable) focusable.focus()
    this.dismiss = (ev) => {
      if (!ev.target.closest("[data-ef-menu], #flowbite_container_pickers")) this.closeMenus()
    }
    this.escape = (ev) => { if (ev.key === "Escape") this.closeMenus() }
    document.addEventListener("mousedown", this.dismiss)
    document.addEventListener("keydown", this.escape)
  }

  alignMenu(menu) {
    menu.classList.add("left-0")
    menu.classList.remove("right-0")
    const limit = window.innerWidth - 4
    if (menu.getBoundingClientRect().right > limit) {
      menu.classList.remove("left-0")
      menu.classList.add("right-0")
    }
  }

  closeMenus() {
    this.element.querySelectorAll("[data-ef-panel]").forEach((panel) => {
      panel.classList.add("hidden")
      this.revertOptions(panel)
    })
    if (this.dismiss) { document.removeEventListener("mousedown", this.dismiss); this.dismiss = null }
    if (this.escape) { document.removeEventListener("keydown", this.escape); this.escape = null }
  }

  // --- options search ------------------------------------------------------

  filterOptions(event) {
    const panel = event.currentTarget.closest("[data-ef-panel]")
    if (!panel) return
    const term = event.currentTarget.value.trim().toLowerCase()
    let shown = 0
    panel.querySelectorAll("[data-ef-option]").forEach((option) => {
      const match = !term || option.dataset.efOption.includes(term)
      option.classList.toggle("hidden", !match)
      if (match) shown += 1
    })
    const empty = panel.querySelector("[data-ef-empty]")
    if (empty) empty.classList.toggle("hidden", shown > 0)
  }

  // --- option checkboxes ---------------------------------------------------

  // Closing a menu without applying leaves the button count showing what is
  // applied, so the boxes have to go back to what the server rendered.
  revertOptions(panel) {
    panel.querySelectorAll("input[type=checkbox]").forEach((box) => { box.checked = box.defaultChecked })
  }

  resetOptions(event) {
    const panel = event.currentTarget.closest("[data-ef-panel]")
    if (!panel) return
    panel.querySelectorAll("input[type=checkbox]").forEach((box) => { box.checked = true })
  }

  clearOptions(event) {
    const panel = event.currentTarget.closest("[data-ef-panel]")
    if (!panel) return
    panel.querySelectorAll("input[type=checkbox]").forEach((box) => { box.checked = false })
  }
}
